import React, { useEffect, useState } from "react"
import { CheckCircle2, ChevronDown, ExternalLink, Link2, MessageCircle, RefreshCw, ShieldCheck, Unplug, UserPlus } from "lucide-react"
import { api, json, pollJob } from "../api"
import { Badge, Button, Card } from "../ui"

export function GroupMePanel({ team, onError }) {
  const [settings, setSettings] = useState(null)
  const [history, setHistory] = useState([])
  const [groups, setGroups] = useState([])
  const [selectedGroup, setSelectedGroup] = useState("")
  const [notice, setNotice] = useState(null)
  const [busy, setBusy] = useState(false)
  const [groupsLoading, setGroupsLoading] = useState(false)
  const [groupsLoaded, setGroupsLoaded] = useState(false)
  const [draft, setDraft] = useState("")
  const groupMeClientId = document.querySelector('meta[name="groupme-client-id"]')?.content
  const accountConnected = Boolean(settings?.access_token_configured)
  const connectedGroup = groups.find(group => String(group.id) === String(settings?.group_id))
  const connectedGroupName = connectedGroup?.name || (groupsLoading ? "Checking connected chat…" : "Connected GroupMe chat")

  useEffect(() => {
    let active = true
    Promise.all([api(`/teams/${team.id}/groupme_setting`), api(`/teams/${team.id}/groupme_messages`)])
      .then(([setting, messages]) => { if (active) { setSettings(setting); setHistory(messages) } })
      .catch(onError)
    return () => { active = false }
  }, [team.id, onError])

  useEffect(() => {
    const url = new URL(window.location.href)
    if (url.searchParams.get("groupme") !== "connected") return
    setNotice("GroupMe account connected. Now choose an existing group or create a new one for this team.")
    url.searchParams.delete("groupme")
    window.history.replaceState({}, "", `${url.pathname}${url.search}`)
  }, [])

  useEffect(() => {
    if (!settings?.access_token_configured || groupsLoaded) return
    let active = true
    setGroupsLoading(true)
    api(`/teams/${team.id}/groupme/groups`)
      .then(available => {
        if (!active) return
        setGroups(available)
        setSelectedGroup(available[0]?.id || "")
      })
      .catch(onError)
      .finally(() => {
        if (!active) return
        setGroupsLoaded(true)
        setGroupsLoading(false)
      })
    return () => { active = false }
  }, [onError, settings?.access_token_configured, team.id])

  async function run(action) {
    setBusy(true); setNotice(null)
    try { await action() } catch (caught) { onError(caught) } finally { setBusy(false) }
  }

  function save(event) {
    event.preventDefault()
    const values = Object.fromEntries(new FormData(event.currentTarget))
    values.enable_game_reminders = values.enable_game_reminders === "on"
    values.enable_practice_reminders = values.enable_practice_reminders === "on"
    run(async () => {
      const updated = await api(`/teams/${team.id}/groupme_setting/upsert`, json("PUT", { groupme_setting: values }))
      setSettings(updated); setNotice("GroupMe settings saved. Credentials remain hidden after saving.")
    })
  }

  async function loadGroups() {
    setGroupsLoading(true)
    setNotice(null)
    try {
      const available = await api(`/teams/${team.id}/groupme/groups`)
      setGroups(available); setSelectedGroup(available[0]?.id || "")
      setGroupsLoaded(true)
      setNotice(available.length ? `Found ${available.length} GroupMe group${available.length === 1 ? "" : "s"}.` : "No GroupMe groups were found for this account.")
    } catch (caught) {
      onError(caught)
    } finally {
      setGroupsLoading(false)
    }
  }

  function connectAccount() {
    if (!groupMeClientId) {
      onError(new Error("GroupMe OAuth is not configured for this environment."))
      return
    }
    window.sessionStorage.setItem("groupme_oauth_team_id", team.id)
    const params = new URLSearchParams({ client_id: groupMeClientId })
    window.location.assign(`https://oauth.groupme.com/oauth/authorize?${params}`)
  }

  function connect() {
    if (!selectedGroup) return
    run(async () => {
      const result = await api(`/teams/${team.id}/groupme/connect`, json("POST", { group_id: selectedGroup, bot_name: settings.bot_name }))
      setSettings(result.setting); setNotice("Connected this team to the selected GroupMe group.")
    })
  }

  function createGroup(event) {
    event.preventDefault()
    const form = event.currentTarget
    const values = Object.fromEntries(new FormData(form))
    run(async () => {
      const result = await api(`/teams/${team.id}/groupme/groups`, json("POST", values))
      setSettings(result.setting); setNotice(`Created and connected “${result.group.name}”.`); form.reset()
    })
  }

  function syncRoster() {
    run(async () => {
      let result = await api(`/teams/${team.id}/groupme/sync_roster`, json("POST", {}))
      if (result.status_url) result = await pollJob(result.status_url)
      const invited = result.results.filter(item => item.status === "invited").length
      const skipped = result.results.filter(item => item.status === "skipped").length
      const failed = result.results.filter(item => item.status === "failed").length
      setNotice(`Roster sync invited ${invited} contact${invited === 1 ? "" : "s"}${skipped ? `; ${skipped} already present` : ""}${failed ? `; ${failed} failed` : ""}.`)
    })
  }

  function disconnect() {
    if (!window.confirm("Disconnect GroupMe and remove the stored access token and bot details?")) return
    run(async () => {
      await api(`/teams/${team.id}/groupme`, { method: "DELETE" })
      setSettings({}); setGroups([]); setSelectedGroup(""); setGroupsLoaded(false); setNotice("GroupMe disconnected and stored credentials removed.")
    })
  }

  function send(event) {
    event.preventDefault()
    const messageContent = draft
    run(async () => {
      let message = await api(`/teams/${team.id}/groupme_messages`, json("POST", { groupme_message: { message_type: "manual", message_content: messageContent } }))
      if (message.status_url) message = await pollJob(message.status_url)
      setHistory(current => [message, ...current]); setNotice("Message sent to GroupMe.")
      setDraft("")
    })
  }

  if (!settings) return <Card className="panel"><p>Loading GroupMe settings…</p></Card>

  const botConnected = Boolean(settings.bot_configured)

  return <div className="groupme-page">
    {notice ? <p className="groupme-notice" role="status">{notice}</p> : null}

    <Card className="groupme-overview">
      <div className="groupme-overview__copy">
        <MessageCircle size={16} aria-hidden="true" />
        <div>
          <p className="eyebrow">Team communication</p>
          <h2>{botConnected ? "GroupMe is ready" : accountConnected ? "Choose your team chat" : "Connect GroupMe"}</h2>
          <p>{botConnected ? <>Updates from {team.name} go to <strong>{connectedGroupName}</strong>.</> : accountConnected ? "Your account is connected. Choose an existing chat or create one for this team." : "Connect once, then keep schedules, reminders, and family updates together."}</p>
        </div>
      </div>
      <Badge tone={botConnected ? "green" : accountConnected ? "blue" : "neutral"}>{botConnected ? "Ready" : accountConnected ? "Account connected" : "Not connected"}</Badge>
      <ol className="groupme-progress" role="list" aria-label="GroupMe setup progress">
        <li className={accountConnected ? "groupme-progress__done" : "groupme-progress__current"}><span>{accountConnected ? <CheckCircle2 size={16} /> : "1"}</span><div><strong>Account</strong><small>{accountConnected ? "Authorized" : "Connect with OAuth"}</small></div></li>
        <li className={botConnected ? "groupme-progress__done" : accountConnected ? "groupme-progress__current" : ""}><span>{botConnected ? <CheckCircle2 size={16} /> : "2"}</span><div><strong>Team chat</strong><small>{botConnected ? "Connected" : "Choose or create"}</small></div></li>
        <li className={botConnected ? "groupme-progress__current" : ""}><span>3</span><div><strong>Updates</strong><small>{botConnected ? "Ready to send" : "Available after setup"}</small></div></li>
      </ol>
    </Card>

    {!accountConnected ? <Card className="groupme-connect-panel">
      <ShieldCheck size={16} aria-hidden="true" />
      <div><h3>Authorize with GroupMe</h3><p>You will sign in on GroupMe and return here automatically. No access token needs to be copied or pasted.</p></div>
      {groupMeClientId ? <Button type="button" onClick={connectAccount} disabled={busy}><ExternalLink size={16} /> Continue with GroupMe</Button> : <Badge tone="orange">OAuth not configured</Badge>}
      {!groupMeClientId ? <p className="form-help form-help--warning">Add <code>groupme.client_id</code> to Rails credentials and restart the app.</p> : null}
    </Card> : null}

    {accountConnected && !botConnected ? <Card className="groupme-setup-panel">
      <div className="groupme-section-heading"><div><p className="eyebrow">Team chat</p><h3>Where should updates go?</h3><p>Use a chat your families already know, or start a fresh one for this season.</p></div><Button type="button" variant="secondary" onClick={loadGroups} disabled={groupsLoading}><RefreshCw size={16} /> {groupsLoading ? "Loading" : "Refresh groups"}</Button></div>
      <div className="groupme-group-options">
        <section>
          <h4>Use an existing chat</h4>
          <p>Select one of the chats available to your GroupMe account.</p>
          {groupsLoading ? <p className="groupme-loading">Loading your GroupMe chats…</p> : groups.length ? <div className="groupme-option-form"><label>GroupMe chat<span className="groupme-select"><select name="group_id" value={selectedGroup} onChange={event => setSelectedGroup(event.target.value)}>{groups.map(group => <option key={group.id} value={group.id}>{group.name}</option>)}</select><ChevronDown size={16} aria-hidden="true" /></span></label><Button type="button" onClick={connect} disabled={busy || !selectedGroup}><Link2 size={16} /> Connect this chat</Button></div> : <p className="groupme-empty-note">No chats found. Refresh the list or create a new one.</p>}
        </section>
        <section>
          <h4>Create a team chat</h4>
          <p>Start a dedicated chat with a clear name for this team and season.</p>
          <form className="groupme-option-form" onSubmit={createGroup}><label>Chat name<input name="name" defaultValue={`${team.name} families`} required /></label><label>Description<input name="description" defaultValue={team.season} /></label><Button type="submit" variant="secondary" disabled={busy}>Create and connect</Button></form>
        </section>
      </div>
      <div className="groupme-setup-footer"><span>Connected to the wrong account?</span><Button type="button" variant="secondary" onClick={connectAccount} disabled={busy}><ExternalLink size={16} /> Reconnect account</Button></div>
    </Card> : null}

    {botConnected ? <div className="groupme-workspace">
      <Card className="groupme-composer">
        <div className="groupme-section-heading"><div><p className="eyebrow">Team updates</p><h3>Write an update</h3><p>Start from an upcoming event or write a quick note to the team.</p></div><Badge tone="green">Connected</Badge></div>
        <label className="groupme-event-picker">Start from an event<span className="groupme-select"><select name="event_template" defaultValue="" onChange={event => { const item = [...team.games.map(game => ({ ...game, type: "Game", date: game.game_date })), ...team.practices.map(practice => ({ ...practice, type: "Practice", date: practice.practice_date }))].find(candidate => `${candidate.type}-${candidate.id}` === event.target.value); if (item) setDraft(formatEventReminder(team, item)) }}><option value="">Choose an upcoming game or practice</option>{[...team.games.map(item => ({ ...item, type: "Game", date: item.game_date })), ...team.practices.map(item => ({ ...item, type: "Practice", date: item.practice_date }))].filter(item => new Date(item.date) >= new Date()).sort((a, b) => new Date(a.date) - new Date(b.date)).map(item => <option key={`${item.type}-${item.id}`} value={`${item.type}-${item.id}`}>{item.type === "Game" ? "🚨" : "⚾"} {item.type}: {item.opponent || item.location || "Team practice"} · {new Date(item.date).toLocaleDateString()}</option>)}</select><ChevronDown size={16} aria-hidden="true" /></span></label>
        <form className="groupme-message-form" onSubmit={send}><label>Message<textarea name="message_content" rows="6" value={draft} onChange={event => setDraft(event.target.value)} placeholder="Share a schedule change, reminder, or team update." required /></label><div className="groupme-composer__actions"><p>Messages are sent to the connected team chat.</p><Button type="submit" disabled={busy || !draft.trim()}><MessageCircle size={16} /> Send to GroupMe</Button></div></form>
        <section className="groupme-history"><div><h4>Recent updates</h4><p>Your latest GroupMe messages from Baseball Hub.</p></div>{history.length ? <ul role="list">{history.slice(0, 6).map(message => <li key={message.id}><div><Badge tone={message.status === "sent" ? "green" : "orange"}>{message.status}</Badge>{message.sent_at ? <time dateTime={message.sent_at}>{new Date(message.sent_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</time> : null}</div><p>{message.message_content}</p></li>)}</ul> : <p className="groupme-empty-note">No updates have been sent yet.</p>}</section>
      </Card>

      <Card className="groupme-control-panel">
        <section>
          <p className="eyebrow">Automation</p><h3>Reminder settings</h3><p>Choose when Baseball Hub should remind families about games and practices.</p>
          <form className="groupme-settings-form" onSubmit={save}><label>Bot name<input name="bot_name" defaultValue={settings.bot_name || "Baseball Hub"} /></label><div className="form-grid"><label>Game reminder<input name="game_reminder_hours" type="number" min="0" defaultValue={settings.game_reminder_hours ?? 24} /><small>Hours before</small></label><label>Practice reminder<input name="practice_reminder_hours" type="number" min="0" defaultValue={settings.practice_reminder_hours ?? 24} /><small>Hours before</small></label></div><div className="check-pair"><label className="check-row"><input name="enable_game_reminders" type="checkbox" defaultChecked={settings.enable_game_reminders ?? true} /> Game reminders</label><label className="check-row"><input name="enable_practice_reminders" type="checkbox" defaultChecked={settings.enable_practice_reminders ?? true} /> Practice reminders</label></div><Button type="submit" variant="secondary" disabled={busy}>Save reminder settings</Button></form>
        </section>
        <section>
          <p className="eyebrow">Families</p><h3>Invite roster contacts</h3><p>Add parent and guardian phone numbers from the roster. Existing members are skipped.</p><Button type="button" variant="secondary" onClick={syncRoster} disabled={busy}><UserPlus size={16} /> Invite roster contacts</Button>
        </section>
        <section>
          <p className="eyebrow">Connection</p><h3>{connectedGroupName}</h3><p>This is the GroupMe chat receiving updates from {team.name}. Reconnect to change accounts, or disconnect to remove the stored credential and bot details.</p><div className="groupme-connection-actions"><Button type="button" variant="secondary" onClick={connectAccount} disabled={busy}><ExternalLink size={16} /> Reconnect</Button><Button type="button" variant="secondary" onClick={disconnect} disabled={busy}><Unplug size={16} /> Disconnect</Button></div>
        </section>
      </Card>
    </div> : null}
  </div>
}

function formatEventReminder(team, event) {
  const date = new Date(event.date)
  const timeZone = team.timezone || "America/New_York"
  const formattedDate = date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", timeZone })
  const formattedTime = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone })

  if (event.type === "Game") {
    return [
      "🚨 GAME REMINDER 🚨",
      "",
      `📅 ${formattedDate}`,
      `⏰ ${formattedTime}`,
      `🆚 vs ${event.opponent}`,
      event.field_number ? `📍 Field ${event.field_number}` : null,
    ].filter(line => line !== null).join("\n")
  }

  return [
    "⚾ PRACTICE REMINDER ⚾",
    "",
    `📅 ${formattedDate}`,
    `⏰ ${formattedTime}`,
    event.location ? `📍 ${event.location}` : null,
    event.notes ? `📝 ${event.notes}` : null,
  ].filter(line => line !== null).join("\n")
}
